import React, { useState, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { get, size, sortBy, take } from 'global/lib/lodash';
import { toBase64, getImageDims, guid } from 'global/utils';
import { Container, ContentContainer, FilesContainer } from './styles';
import { Icon } from '@mui/material';
import SnackAlerts from 'global/components/snack-alerts';
import DragSortableList from '../sortable-list';
import { _size } from '../../utils';
import { DOC_ACCEPT } from '../../utils/mediaUtils';
import { useIntersect } from '../../hooks/useIntersect';
import useDebounce from '../../hooks/useDebounce';

export const MAXSIZE = 1024 * 1024 * 384;
export const FILEID = '_file_';

export const isFile = (f) => get(f, 'id', '').indexOf(FILEID) > -1;

const takeAdd = 20;

export const DropArea = (props) => {
  const [files, setFiles] = useState(props.files || []);
  const [sortableFiles, setSortableFiles] = useState(props.files || []);
  const [takeAmt, setTakeAmt] = useState(0);
  const [alerts, setAlerts] = useState();

  const [ref, entry] = useIntersect({});
  const inView = get(entry, 'isIntersecting', false);

  // This minor delay makes the following hook get called only after items are rendered.
  // It makes inView (bool visibility of the observer) more accurate when it's called.
  const _takeAmt = useDebounce(takeAmt, 100);
  useEffect(() => {
    if (takeAmt == 0 || (!!size(files) && inView && takeAmt < size(files))) {
      setTakeAmt(takeAmt + takeAdd);
    }
  }, [inView, _takeAmt]);

  const onDrop = async (acceptedFiles, failed) => {
    if (size(failed)) {
      const isTooBig = failed.filter((f) => f.size >= (props.maxFileSize || MAXSIZE));
      setAlerts({
        message: size(isTooBig) ? `${size(failed)} files failed to upload. File is too big.` : `${size(failed)} files failed to upload`,
        severity: 'error',
      });
    }
    // In docMode (files/documents only), reject image and video files
    if (props.docMode) {
      const docFiles = [];
      const imgRejected = [];
      acceptedFiles.forEach((f) => {
        if (f.type.includes('image') || f.type.includes('video')) {
          imgRejected.push(f);
        } else {
          docFiles.push(f);
        }
      });
      if (imgRejected.length) {
        setAlerts({
          message: `${imgRejected.length} image/video file(s) not allowed. Only documents are accepted.`,
          severity: 'warning',
        });
      }
      acceptedFiles = docFiles;
    }

    const errors = [];
    const rejected = [];
    const _files = [];
    await Promise.all(
      acceptedFiles.map(async (file, index) => {
        const acceptArr = get(props, 'accept', []) || [];
        const acceptImageOrVideo = acceptArr.includes('image/*') || acceptArr.includes('video/*');
        const fileIsImgOrVideo = file.type.includes('image') || file.type.includes('video');
        // In "both" mode (accept is null/undefined/empty), check each file individually
        const shouldPreview = acceptArr === null || acceptArr === undefined || (Array.isArray(acceptArr) && acceptArr.length === 0) ? fileIsImgOrVideo : acceptImageOrVideo && fileIsImgOrVideo;
        if (shouldPreview) {
          // When `noBase64` is set, generate the preview via an object URL
          // instead of base64-encoding the whole file. This avoids loading the
          // entire image into memory as a base64 string, which is wasteful
          // since uploads go directly to S3 (see redux/actions/imageUploader).
          if (props.noBase64) {
            try {
              const data = URL.createObjectURL(file);
              _files.push({ file: file, id: file.id || `${FILEID}${guid()}`, data });
            } catch (e) {
              errors.push(e);
            }
          } else {
            const data = await toBase64(file).catch((e) => {
              errors.push(e);
            });
            if (!data) {
              return;
            }
            _files.push({ file: file, id: file.id || `${FILEID}${guid()}`, data });
          }
        } else {
          _files.push({ file: file, id: file.id || `${FILEID}${guid()}` });
        }
      })
    );
    let allFiles = [...files, ..._files];

    // Enforce maxFiles limit on the total accumulated files
    const maxFiles = get(props, 'maxFiles');
    if (maxFiles && allFiles.length > maxFiles) {
      const dropped = allFiles.length - maxFiles;
      allFiles = allFiles.slice(0, maxFiles);
      setAlerts({
        message: `Maximum of ${maxFiles} files allowed. ${dropped} file(s) were not added.`,
        severity: 'warning',
      });
    }

    setFiles(allFiles);
    setTakeAmt((prev) => Math.max(prev, allFiles.length));
    const filesChange = get(props, 'onFilesChange', () => 0);
    filesChange(allFiles);

    if (size(errors)) {
      const hasAlert = props.onAlert;
      const message = errors.map((e) => e.message).join(', ');
      if (hasAlert) {
        props.onAlert && props.onAlert(message, 'overwrite');
      } else {
        setAlerts({
          message,
          severity: 'error',
        });
      }
    }
  };

  useEffect(() => {
    if (props.sortable) {
      setSortableFiles(
        files.map((file, i) => {
          const renderFile = get(props, 'renderFile', () => null);
          const key = `${get(file, 'id', `key_${i}`)}`;
          const sortableFile = sortableFiles.find((f) => f.key === key);
          return { ...sortableFile, content: renderFile(file, key, i), key };
        })
      );
    }
  }, [files]);

  const onSort = (order, event) => {
    const rankedOrder = sortBy([...order], 'rank')
      .map((o) => files.find((f) => f.id == o.key))
      .map((f, i) => ({ ...f, order_column: i }));
    setSortableFiles(order);
    props.onSort && props.onSort(rankedOrder, event);
  };

  useEffect(() => {
    setFiles(props.files || []);
  }, [props.files]);

  // PFM-2600: revoke any object URLs we created for previews when the
  // component unmounts, so the underlying blobs can be garbage collected.
  // Track the latest files in a ref so the unmount cleanup sees them (an
  // empty-deps useEffect would close over the initial `files` array).
  const filesRef = useRef(files);
  useEffect(() => {
    filesRef.current = files;
  }, [files]);
  useEffect(() => {
    return () => {
      if (!props.noBase64) return;
      (filesRef.current || []).forEach((f) => {
        const d = get(f, 'data', '');
        if (typeof d === 'string' && d.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(d);
          } catch (_) {}
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const maxFiles = get(props, 'maxFiles');
  const atMaxFiles = maxFiles && files.length >= maxFiles;

  // In docMode, restrict the file picker to common document types so images/videos are greyed out
  const docAccept = props.docMode ? DOC_ACCEPT : undefined;

  const { getRootProps, getInputProps } = useDropzone({
    maxSize: props.maxFileSize || MAXSIZE,
    maxFileSize: MAXSIZE,
    ...props,
    onDrop,
    accept: docAccept || props.accept,
    disabled: atMaxFiles || props.disabled,
  });

  return (
    <Container {...getRootProps()} className={props.className} style={atMaxFiles || props.disabled ? { ...props.style, cursor: 'default' } : props.style}>
      <input {...getInputProps()} />

      {!atMaxFiles && (
        <ContentContainer>
          <Icon color={'inherit'}>cloud_upload</Icon>
          {get(props, 'renderContent', () => null)()}
        </ContentContainer>
      )}

      {files.length > 0 && !props.sortable && (
        <FilesContainer>
          {take(files, takeAmt).map((file, i) => {
            const renderFile = get(props, 'renderFile', () => null);
            return renderFile(file, i);
          })}
          <div ref={ref} />
        </FilesContainer>
      )}

      {!!_size(files) && props.sortable && (
        <DragSortableList
          items={sortableFiles}
          dropBackTransitionDuration={0.3}
          onSort={onSort}
          placeholder={<div className={`placeholderContent`}>DROP HERE</div>}
          type={'grid'}
          bubbleEmptyClicks={props.bubbleEmptyClicks}
        />
      )}

      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </Container>
  );
};

export default DropArea;
